import type { ComponentProps, FC } from 'react';
import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';

import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { getTitleContentProperty } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { VIEW_PAGE_BYLINE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import {
	ReloadType,
	useQuickReloadSubscriber,
} from '@confluence/quick-reload/entry-points/subscription.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop';

import {
	default as ContentHeaderUnifiedQuery,
	type ContentHeaderUnifiedQuery$data as ContentHeaderUnifiedQueryType,
} from '../__generated__/ContentHeaderUnifiedQuery.graphql.ts';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from '../defaultQueryVariables.js';
import { BYLINE_METRIC } from '../perf.config.js';

import type { ExternalByLineQuery$data as ExternalByLineQueryType } from './__generated__/ExternalByLineQuery.graphql.ts';
import { ByLinePlaceholder } from './ByLinePlaceholder.tsx';
import type { QueryType, QueryVariablesType } from './byLineTypes.ts';
import { getSourceTemplateKey, isFabricEditor, toUserProps } from './byLineUtils.tsx';
import { ByLineWithContributorsComponent } from './ByLineWithContributorsComponent.tsx';
import { ExternalByLineQuery } from './ExternalByLineQuery.graphql.ts';

type ContentHeaderUnifiedQuery_content_PaginatedContentListWithChild_nodes_Content = NonNullable<
	NonNullable<NonNullable<ContentHeaderUnifiedQueryType>['content']>['nodes']
>[0];

// WARN: This is temporary code to send analytics event when we would crash the part
// of the ByLine experience when byline user is undefined due to any reason.
// This is done as part of https://ops.internal.atlassian.com/jira/browse/HOT-106424
// investigation. This code should be removed once we know the root cause/impact.
const HOT106424Tracker: FC<{
	content:
		| ExternalByLineQueryType['singleContent']
		| NonNullable<NonNullable<ContentHeaderUnifiedQueryType['content']>['nodes']>[0]
		| null
		| undefined;
	contentId: string;
	contentContributors: QueryType['contentContributors'];
	isExternalShareRequest: boolean;
}> = ({ content, contentId, contentContributors, isExternalShareRequest }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const byUser = content?.version?.by;
		const createdBy = content?.history?.createdBy;
		const hasEmptyContributors = (contentContributors?.nodes || []).some((node) => !node);
		const history = content?.history;
		const createdDate = content?.history?.createdDate;

		if (hasEmptyContributors || !createdBy || !byUser) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'byLine',
					action: 'byLineUserEmpty',
					actionSubject: 'content',
					attributes: {
						contentId,
						isExternalShareRequest,
						hasEmptyContributors,
						hasCreatedBy: Boolean(createdBy),
						hasByUser: Boolean(byUser),
						hasContent: Boolean(content),
						hasHistory: Boolean(history),
						hasCreatedDate: Boolean(createdDate),
					},
				},
			}).fire();
		}
	}, [content, contentId, contentContributors, createAnalyticsEvent, isExternalShareRequest]);

	return null;
};

type ByLineProps = {
	/**
	 * ID of the content to render
	 */
	contentId: string;
	/**
	 * Version to render
	 */
	versionOverride?: number | null;
	/**
	 * Control which version should be rendered for embedded content (attachments, "page-include" macro, etc.).
	 * 'version-at-save': Render the exact version when page was saved.
	 * 'current': Default. Render the latest version.
	 */
	embeddedContentRender?: string | null;
	/**
	 * Determines if we should display reactions in the byline
	 */
	shouldShowBylineReactions?: boolean;
	/**
	 * Content type to render
	 */
	contentType?: string | null;
	/**
	 * Content space id
	 */
	spaceId?: string | null;
	/**
	 * Content sub type to render
	 */
	contentSubType?: string | null;
};

type PassThroughProps = Pick<
	ComponentProps<typeof ByLineWithContributorsComponent>,
	'hasByLineContributors' | 'hasByLineExtensions'
>;

export const ByLine: FC<ByLineProps & PassThroughProps> = ({
	contentId,
	versionOverride,
	embeddedContentRender,
	shouldShowBylineReactions,
	contentType,
	spaceId,
	contentSubType,
	...passThroughProps
}) => {
	/**
	 * Initially, only request enough contributors to populate displayed
	 * avatars of the ByLineWithContributorsComponent's AvatarGroup.
	 * This is four at most; at five it shows three and a '+2' button.
	 * Additional contributors will be requested on-demand when hovering
	 * over/clicking its '+2' (or whatever) button.
	 *
	 * Additional Note: Please update the default limit in the relevant query to match
	 * DEFAULT_CONTRIBUTORS_FETCH_LIMIT. Preloader will not work as expected if these
	 * limits do not match.
	 */
	const [contributorsFetchLimit, setContributorsFetchLimit] = useState(
		DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
	);

	const [spaceKey] = usePageSpaceKey();
	const { isExternalShareRequest } = useContext(ExternalShareContext);

	const lastLoadedQueryData = useRef<QueryType | null>(null);

	const fetchAllContributors = () => {
		if (lastLoadedQueryData.current) {
			// The lastLoadedQueryData would be from the initial request with the
			// fetch limit of 4
			setContributorsFetchLimit(
				lastLoadedQueryData.current?.contentContributors?.count || Number.MAX_SAFE_INTEGER,
			);
		}
	};

	// eslint-disable-next-line prefer-const
	let { data, loading, error, refetch } = useQueryInterop<
		QueryType,
		QueryVariablesType & { cacheBuster?: string }
	>(isExternalShareRequest ? ExternalByLineQuery : ContentHeaderUnifiedQuery, {
		variables: {
			contentId,
			spaceKey,
			// Don't omit versionOverride, embeddedContentRender and limit
			// It's an Apollo bug that if preloader these needs to be match preloaded query
			// Also doesn't work if removing them from preloader variables HOT-109035
			versionOverride: versionOverride || null,
			embeddedContentRender: embeddedContentRender || null,
			limit: contributorsFetchLimit,
			useNewContentTopper: true,
			includeGeneratedBy: fg('cc-activation-user-generated-by-byline'),
		},
		context: {
			allowOnExternalPage: true,
			single: true,
		},
	});

	const reload = useCallback(
		// Forcing refetch by passing dummy variable. Known Apollo bug (ノಠ益ಠ)ノ彡┻━┻, see page below:
		// https://pug.jira-dev.com/wiki/spaces/~683304838/pages/5971970200/Apollo+Client+-+weird+refetch+behavior
		// Reason using static cacheBuster: "":
		// ContentTitle component also makes ContentHeaderUnifiedQuery and refetch.
		// If using different cache buster values, the refetch in each component will make its owen refetch network call.
		// So setting a static value for this and ContentTitle component to make refetch network call only once.
		// And a static cache buster value can also force refetch to ignore cache of original query.
		() => refetch({ contentId, cacheBuster: '' }),

		[refetch, contentId],
	);

	useQuickReloadSubscriber({
		name: 'byline',
		types: [ReloadType.content],
		reload,
	});

	const renderByLine = () => {
		if (loading) {
			// If we're loading because the ByLineWithContributorsComponent called our
			// fetchAllContributors, then we should continue the render with data
			// cached from the previous query (the one with the low
			// contributorsFetchLimit). It's important to not bring the skeletons back
			// up, as that'd kill the opening of the dropdown.
			if (lastLoadedQueryData.current) {
				data = lastLoadedQueryData.current;
			} else {
				return <ByLinePlaceholder />;
			}
		} else {
			if (!error) lastLoadedQueryData.current = data || null;
		}

		const content = isExternalShareRequest
			? (data as ExternalByLineQueryType)?.singleContent
			: (data as ContentHeaderUnifiedQueryType)?.content?.nodes?.[0];

		if (error) {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
				return null;
			} else {
				// TODO: unknown errors should have a nicer UI within `<ErrorDisplay>`, but we don't have such UI for now
				// so returning empty `<ErrorDisplay>` here.
				return <ErrorDisplay error={error} />;
			}
		} else if (!content) {
			return null;
		}

		const author = toUserProps(content?.history?.createdBy);
		const owner =
			content?.history?.ownedBy && content?.type === 'page'
				? toUserProps(content?.history?.ownedBy)
				: null;
		const lastOwner =
			content?.history?.lastOwnedBy && content?.type === 'page'
				? toUserProps(content?.history?.lastOwnedBy)
				: null;
		const createdDateString = content?.history?.createdDate;
		const createdDate = createdDateString ? new Date(createdDateString) : null;
		const by = toUserProps(content?.version?.by);
		// @ts-expect-error -- `ExternalByLineQuery` does not query for version messages; this should later be refactored to Relay fragments in the actual components
		const versionComment = content?.version?.message || undefined;
		const contributors = data?.contentContributors?.nodes?.map(toUserProps) || [];
		const contributorsCount = data?.contentContributors?.count || 0;
		const isOwnerContributor = data?.contentContributors?.isOwnerContributor || false;

		const templateId = !isExternalShareRequest ? getSourceTemplateKey(data) : null;
		const isFabricPage = !isExternalShareRequest ? isFabricEditor(data) : false;
		const { titleContentProperties } = getTitleContentProperty(data);
		const generatedBy = !isExternalShareRequest
			? (content as ContentHeaderUnifiedQuery_content_PaginatedContentListWithChild_nodes_Content)
					?.contentProperties?.latest?.generatedBy
			: undefined;

		const componentProps = {
			contentId,
			author,
			owner,
			createdDate,
			contributors,
			contributorsCount,
			isOwnerContributor,
			versionComment,
			fetchAllContributors,
			templateId,
			isFabricPage,
			lastOwner,
			shouldShowBylineReactions,
			contentSubType,
			spaceId,
			contentType,
			titleContentProperties,
			generatedBy,
			version: {
				friendlyWhen: content?.version?.friendlyWhen || '',
				number: Number(content?.version?.number || ''),
				by,
				// @ts-expect-error -- `ContentHeaderUnifiedQuery` does not query for `syncRev`; this should later be refactored to Relay fragments in the actual components
				syncRev: content?.version?.syncRev || 0,
				// @ts-expect-error -- `ContentHeaderUnifiedQuery` does not query for `confRev`; this should later be refactored to Relay fragments in the actual components
				confRev: content?.version?.confRev || 0,
				contentTypeModified: content?.version?.contentTypeModified || false,
			},
			...passThroughProps,
		};

		return (
			<Fragment>
				<ByLineWithContributorsComponent {...componentProps} />
				{!loading && (
					<PageSegmentLoadEnd
						key={contentId}
						metric={BYLINE_METRIC}
						customData={{ contributorsCount }}
					/>
				)}
				<HOT106424Tracker
					content={content}
					contentId={contentId}
					contentContributors={data?.contentContributors}
					isExternalShareRequest={isExternalShareRequest}
				/>
			</Fragment>
		);
	};

	return (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			<ExperienceStart name={VIEW_PAGE_BYLINE_EXPERIENCE} id={contentId} />
			<PageSegmentLoadStart key={contentId} metric={BYLINE_METRIC} />
			{renderByLine()}
		</ErrorBoundary>
	);
};
